// AdminDashboard.js
import { useState, useEffect } from "react";
import { useNavigate, Link, Outlet } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaUsers, FaUserShield, FaChartBar, FaCalendarAlt, FaEnvelope, FaCog, FaDollarSign, FaCreditCard, FaCoins, FaGift, FaMoneyCheckAlt, FaExchangeAlt, FaUserPlus, FaCheckCircle, FaLock } from "react-icons/fa"; // Thêm FaLock

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, fetchWithRetry, isLoading, logout } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    vipUsers: 0,
    reports: 0,
    matches: 0,
    events: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isLoading) return;

    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("token");
        console.log("Token in AdminDashboard:", token);

        const [usersRes, reportsRes] = await Promise.all([
          fetchWithRetry({ method: "get", url: "/api/admin/users" }),
          fetchWithRetry({ method: "get", url: "/api/admin/reports" }),
        ]);

        console.log("Users response:", usersRes.data);
        console.log("Reports response:", reportsRes.data);
        setStats({
          totalUsers: usersRes.data.length,
          vipUsers: usersRes.data.filter((user) => user.role === "vip").length,
          reports: reportsRes.data.length,
          matches: 0,
          events: 0,
        });
        setLoading(false);
      } catch (error) {
        console.error("Lỗi tải thống kê:", error);
        const errorMessage =
          error.response?.data?.message ||
          (error.response?.status === 403
            ? "Bạn không có quyền truy cập dữ liệu này! Vui lòng đăng nhập lại."
            : "Không thể tải thống kê!");
        console.log("Error response data:", error.response?.data);
        setError(errorMessage);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setTimeout(() => {
            logout();
          }, 3000);
        }
        setLoading(false);
      }
    };

    fetchStats();
  }, [navigate, user, fetchWithRetry, isLoading, logout]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-bold">Bảng điều khiển quản trị</h1>
          <button
            onClick={logout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition duration-200"
          >
            Đăng xuất
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto p-6 flex">
        {/* Sidebar (Điều hướng) */}
        <div className="w-1/4 bg-white p-4 rounded-lg shadow-lg mr-6">
          <div className="grid grid-cols-1 gap-4">
            <Link
              to="user-management"
              className="bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition duration-200 flex items-center space-x-4"
            >
              <FaUsers className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý người dùng</h3>
                <p className="text-xs">Xem, chỉnh sửa, xóa hoặc khóa người dùng</p>
              </div>
            </Link>
            <Link
              to="role-settings"
              className="bg-green-500 text-white p-4 rounded-lg hover:bg-green-600 transition duration-200 flex items-center space-x-4"
            >
              <FaUserShield className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Thiết lập vai trò</h3>
                <p className="text-xs">Quản lý vai trò và quyền hạn</p>
              </div>
            </Link>
            <Link
              to="reports"
              className="bg-red-500 text-white p-4 rounded-lg hover:bg-red-600 transition duration-200 flex items-center space-x-4"
            >
              <FaChartBar className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý báo cáo</h3>
                <p className="text-xs">Xem và xử lý các báo cáo từ người dùng</p>
              </div>
            </Link>
            <Link
              to="event-management"
              className="bg-purple-500 text-white p-4 rounded-lg hover:bg-purple-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCalendarAlt className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý sự kiện</h3>
                <p className="text-xs">Tạo, chỉnh sửa, xóa sự kiện hẹn hò/kết bạn</p>
              </div>
            </Link>
            <Link
              to="admin-messages"
              className="bg-yellow-500 text-white p-4 rounded-lg hover:bg-yellow-600 transition duration-200 flex items-center space-x-4"
            >
              <FaEnvelope className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý tin nhắn</h3>
                <p className="text-xs">Xem và kiểm duyệt tin nhắn riêng tư</p>
              </div>
            </Link>
            <Link
              to="chat-restrictions" // Thêm liên kết mới
              className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center space-x-4"
            >
              <FaLock className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý giới hạn chat</h3>
                <p className="text-xs">Cấu hình giới hạn chat cho các vai trò</p>
              </div>
            </Link>
            <Link
              to="membership-plans"
              className="bg-teal-500 text-white p-4 rounded-lg hover:bg-teal-600 transition duration-200 flex items-center space-x-4"
            >
              <FaDollarSign className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý gói nâng cấp</h3>
                <p className="text-xs">Xem, thêm, sửa, xóa các gói trả phí</p>
              </div>
            </Link>
            <Link
              to="coin-packages"
              className="bg-cyan-500 text-white p-4 rounded-lg hover:bg-cyan-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCoins className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý gói Coin</h3>
                <p className="text-xs">Xem, thêm, sửa, xóa các gói coin</p>
              </div>
            </Link>
            <Link
              to="coin-transactions"
              className="bg-amber-500 text-white p-4 rounded-lg hover:bg-amber-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCoins className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Lịch sử giao dịch Coin</h3>
                <p className="text-xs">Xem lịch sử giao dịch coin của người dùng</p>
              </div>
            </Link>
            <Link
              to="user-coin-balance"
              className="bg-lime-500 text-white p-4 rounded-lg hover:bg-lime-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCoins className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý số dư Coin</h3>
                <p className="text-xs">Xem và chỉnh sửa số dư coin của người dùng</p>
              </div>
            </Link>
            <Link
              to="gift-management"
              className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition duration-200 flex items-center space-x-4"
            >
              <FaGift className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý quà tặng</h3>
                <p className="text-xs">Xem, thêm, sửa, xóa quà tặng</p>
              </div>
            </Link>
            <Link
              to="coin-withdrawal-requests"
              className="bg-pink-500 text-white p-4 rounded-lg hover:bg-pink-600 transition duration-200 flex items-center space-x-4"
            >
              <FaMoneyCheckAlt className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý yêu cầu rút tiền</h3>
                <p className="text-xs">Xem và xử lý yêu cầu rút tiền của người dùng</p>
              </div>
            </Link>
            <Link
              to="coin-exchange-rate"
              className="bg-indigo-500 text-white p-4 rounded-lg hover:bg-indigo-600 transition duration-200 flex items-center space-x-4"
            >
              <FaExchangeAlt className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Thiết lập giá trị quy đổi Coin</h3>
                <p className="text-xs">Cấu hình giá trị quy đổi coin ra tiền mặt</p>
              </div>
            </Link>
            <Link
              to="withdrawal-payment-gateways"
              className="bg-orange-500 text-white p-4 rounded-lg hover:bg-orange-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCreditCard className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Cấu hình cổng thanh toán rút tiền</h3>
                <p className="text-xs">Thiết lập cổng thanh toán cho rút tiền</p>
              </div>
            </Link>
            <Link
              to="payments"
              className="bg-orange-500 text-white p-4 rounded-lg hover:bg-orange-600 transition duration-200 flex items-center space-x-4"
            >
              <FaDollarSign className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý thanh toán</h3>
                <p className="text-xs">Theo dõi trạng thái thanh toán và giao dịch</p>
              </div>
            </Link>
            <Link
              to="payment-gateways"
              className="bg-indigo-500 text-white p-4 rounded-lg hover:bg-indigo-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCreditCard className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Cấu hình cổng thanh toán quốc tế</h3>
                <p className="text-xs">Thiết lập API cho các cổng thanh toán quốc tế</p>
              </div>
            </Link>
            <Link
              to="local-payment-gateways"
              className="bg-pink-500 text-white p-4 rounded-lg hover:bg-pink-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCreditCard className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Cấu hình cổng thanh toán nội địa</h3>
                <p className="text-xs">Thiết lập API cho các cổng thanh toán nội địa</p>
              </div>
            </Link>
            <Link
              to="coin-to-vip-plans"
              className="bg-teal-600 text-white p-4 rounded-lg hover:bg-teal-700 transition duration-200 flex items-center space-x-4"
            >
              <FaUserPlus className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý gói VIP đổi bằng Coin</h3>
                <p className="text-xs">Xem, thêm, sửa, xóa các gói VIP đổi bằng coin</p>
              </div>
            </Link>
            <Link
              to="coin-to-vip-requests"
              className="bg-amber-600 text-white p-4 rounded-lg hover:bg-amber-700 transition duration-200 flex items-center space-x-4"
            >
              <FaCheckCircle className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Quản lý yêu cầu nâng cấp gói VIP</h3>
                <p className="text-xs">Xem và xử lý yêu cầu nâng cấp gói VIP bằng coin</p>
              </div>
            </Link>
            <Link
              to="settings"
              className="bg-gray-500 text-white p-4 rounded-lg hover:bg-gray-600 transition duration-200 flex items-center space-x-4"
            >
              <FaCog className="text-2xl" />
              <div>
                <h3 className="text-md font-semibold">Cài đặt hệ thống</h3>
                <p className="text-xs">Cấu hình quy tắc và giới hạn hệ thống</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Nội dung chính */}
        <div className="w-3/4">
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
            </div>
          ) : (
            <>
              {/* Thống kê */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-200 flex items-center space-x-4">
                  <FaUsers className="text-blue-500 text-3xl" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Tổng số người dùng</h3>
                    <p className="text-2xl font-bold text-gray-600">{stats.totalUsers}</p>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-200 flex items-center space-x-4">
                  <FaUserShield className="text-green-500 text-3xl" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Người dùng VIP</h3>
                    <p className="text-2xl font-bold text-gray-600">{stats.vipUsers}</p>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-200 flex items-center space-x-4">
                  <FaChartBar className="text-red-500 text-3xl" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Báo cáo chưa xử lý</h3>
                    <p className="text-2xl font-bold text-gray-600">{stats.reports}</p>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-200 flex items-center space-x-4">
                  <FaCalendarAlt className="text-purple-500 text-3xl" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Sự kiện đang diễn ra</h3>
                    <p className="text-2xl font-bold text-gray-600">{stats.events}</p>
                  </div>
                </div>
              </div>

              {/* Outlet để render các route con */}
              <Outlet />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;